<div class="flex flex-col h-full w-full">
    <div class="flex justify-between items-center  pt-4 pr-4 pl-4 pb-2 w-full">
        <p class="flex items-center font-semibold text-lg gap-2">
            <span>Overview</span>
        </p>
        <!-- Analysis Summary and Setup have been moved out of this component.
             Analysis Summary is now its own left-panel tab.
             Setup is rendered via the rightActions slot on the tab strip. -->
        <div class="flex gap-3">
            <app-popover #filterPopover placement="bottom" align="end" [width]="'22rem'">
                <ng-template #trigger>
                    <app-button [size]="'default'" variant="outlined" severity="contrast" pTooltip="Filter"
                        tooltipPosition="top">
                        <div class="relative flex items-center justify-center">
                            <app-icon type="filter" source="lucide" size="sm" />
                            @if (hasActiveFilters) {
                            <span
                                class="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-orange-500 border border-white"></span>
                            }
                        </div>
                    </app-button>
                </ng-template>
                <ng-template #body>
                    <div class="flex flex-col p-2 gap-4">

                        <div class="flex items-center justify-between">
                            <span class="font-semibold text-base">Filter</span>
                            <div class="cursor-pointer text-gray-400 hover:text-gray-600"
                                (click)="filterPopover.hide()">
                                <app-icon type="x" source="lucide" size="sm"></app-icon>
                            </div>
                        </div>


                        <div class="flex flex-col gap-2">
                            <span class="text-xs text-gray-400">Rank by</span>
                            <div class="flex items-center gap-3">
                                <span class="text-sm" [class.font-semibold]="filterRankBy === 'cost'">Cost</span>
                                <app-toggle [ngModel]="filterRankBy === 'schedule'" class="mt-1"
                                    (onChange)="filterRankBy = $event ? 'schedule' : 'cost'">
                                </app-toggle>
                                <span class="text-sm"
                                    [class.font-semibold]="filterRankBy === 'schedule'">Schedule</span>
                            </div>
                        </div>


                        <div class="flex items-center gap-2 flex-wrap">
                            @for (n of filterTopNOptions; track n) {
                            <div class="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm bg-gray-200 text-sm cursor-pointer transition-colors"
                                [class.border-gray-800]="filterSelectedTopN === n"
                                [class.font-medium]="filterSelectedTopN === n"
                                [class.border-gray-200]="filterSelectedTopN !== n"
                                [class.text-gray-500]="filterSelectedTopN !== n"
                                (click)="filterSelectedTopN = filterSelectedTopN === n ? null : n">
                                Top {{ n }}
                                @if (filterSelectedTopN === n) {
                                <app-icon type="x" source="lucide" size="xs" class="text-xs"></app-icon>
                                }
                            </div>
                            }
                        </div>

                        <div class="border-t border-gray-100"></div>


                        <div class="flex flex-col gap-1">
                            <span class="text-xs text-gray-400 mb-1">Layers</span>
                            @if (filterLayersLoading) {
                            <div class="flex items-center justify-center py-3">
                                <div class="loader"></div>
                            </div>
                            } @else if (filterLayers.length === 0) {
                            <span class="text-xs text-gray-400 py-2">No layers available</span>
                            } @else {
                            @for (layer of filterLayers; track layer.label) {
                            <div class="flex items-center justify-between py-2">
                                <span class="text-sm">{{ layer.label }}</span>
                                <app-toggle [(ngModel)]="layer.enabled"></app-toggle>
                            </div>
                            }
                            }
                        </div>

                        <div class="border-t border-gray-100"></div>

                        <div class="flex flex-col gap-2">
                            <span class="text-xs text-gray-400">Alternatives</span>
                            <app-multi-select [(value)]="filterSelectedAlternates" [options]="filterAlternateOptions"
                                [config]="alternateMultiSelectConfig">
                            </app-multi-select>
                        </div>

                        <div class="border-t border-gray-100"></div>


                        <div class="flex items-center justify-between">
                            <div class="text-sm text-gray-500 cursor-pointer hover:text-gray-800"
                                (click)="onClearFilter()">Clear all</div>
                            <app-button label="Apply filter" severity="primary" variant="filled"
                                [disabled]="!hasFilterChanged" (clicked)="onApplyFilter(); filterPopover.hide()">
                            </app-button>
                        </div>
                    </div>
                </ng-template>
            </app-popover>
        </div>
    </div>

    <!-- Credits exhausted: the leaderboard/costs are computed downstream of the VE and vendor agents,
         so when one of those runs out of credits nothing here is ever produced and every skeleton
         below would spin forever. No feature/featureId is passed — this surface owns no thread, so
         there is nothing to resume from here; forceExhausted renders the depleted card from the
         balance alone. -->
    @if (isCreditsExhausted) {
    <div class="flex flex-1 items-center justify-center p-4">
        <app-credits-overview variant="exhaustionlayout" [forceExhausted]="isCreditsExhausted" />
    </div>
    } @else {

    <!-- Dashboard content – always visible now that Analysis Summary has its own tab -->
    <div class="flex flex-col  min-h-0 overflow-y-auto p-4 gap-3">

        <!-- Card grows naturally to fit all layer content — no fixed height needed -->
        <div class="bg-white rounded-2xl border border-gray-200/80 p-4 h-full">
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-md font-semibold">Layer visualization</h3>
                <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                    severity="contrast" pTooltip="Expand" tooltipPosition="top"
                    (clicked)="expandedDashboardTable = 'layerVisualization'">
                </app-button>
            </div>

            @if (layerVisualizationLoading) {
            <!-- Skeleton table: border-separate so cell z-index is reliable (same as data table). -->
            <div class="overflow-x-auto scrollbar-thin">
                <div class="flex flex-col min-w-max">
                    <!-- Header row -->
                    <div class="flex items-stretch">
                        <div class="layer-visualization-width shrink-0 pb-4 pr-6 pl-4 flex items-end">
                            <p-skeleton width="6rem" height="1rem" class="rounded-md"></p-skeleton>
                        </div>
                        @for (c of [1,2,3,4,5]; track c) {
                        <div class="w-[9rem] shrink-0 pb-4 px-6 flex items-end justify-center">
                            <p-skeleton width="4.5rem" height="1.5rem" class="rounded-md"></p-skeleton>
                        </div>
                        }
                    </div>
                    <!-- Data rows -->
                    @for (r of [1,2,3,4]; track r) {
                    <div class="flex items-stretch">
                        <div class="layer-visualization-width shrink-0 py-3 pr-6 pl-4 flex items-center">
                            <p-skeleton width="6rem" height="1rem" class="rounded-md"></p-skeleton>
                        </div>
                        @for (c of [1,2,3,4,5]; track c) {
                        <div class="w-[9rem] shrink-0 py-3 px-6 flex items-center justify-center">
                            <p-skeleton width="4.5rem" height="1.5rem" class="rounded-md"></p-skeleton>
                        </div>
                        }
                    </div>
                    }
                </div>
            </div>
            } @else if (filteredLayerVisualizationData)
            {
            <!-- pb-8: last row's method image is position:absolute; bottom:-18px and overhangs the
                 table. overflow-x:auto implicitly clips overflow-y, so bottom padding keeps the
                 method image fully visible without introducing a vertical scrollbar.

                 WHY border-separate (not border-collapse):
                 border-collapse makes the browser own the cell borders and merge them at the
                 table level, which causes per-cell z-index to be ignored on Chromium/Windows.
                 border-separate keeps every cell as an independent normal box — z-index and
                 position:sticky behave the same way as on a plain <div>.
                 border-spacing:0 removes the default cell gap so the visual result is
                 identical to border-collapse. -->
            <div class="overflow-x-auto scrollbar-thin">
                <div class="flex flex-col min-w-max">

                    <!-- HEADER ROW -->
                    <div class="flex items-stretch">
                        <!-- Sticky "Layers" header -->
                        <div class="layer-visualization-width shrink-0 text-left text-sm font-semibold text-gray-700 pb-4 pr-4 pl-4 bg-white sticky left-0 z-50 flex items-end"
                            style="z-index: 50;">
                            Layers1
                        </div>
                        @for (altKey of filteredLayerVisualizationData.alternateKeys; track altKey; let i = $index) {
                        <div
                            class="w-[9rem] shrink-0 text-center pb-4 px-6 flex flex-col items-center justify-end gap-1">
                            @if (filteredLayerVisualizationData.suitableAlternateKey === altKey) {
                            <div class="relative inline-flex flex-col items-center mb-1">
                                <span
                                    class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white theme-bg">
                                    <i class="pi pi-check icon-sm mr-1"></i> Most suitable
                                </span>
                                <span class="block w-0 h-0"
                                    style="border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 6px solid #6b21a8;"></span>
                            </div>
                            }
                            <span class="inline-block w-[7rem] truncate px-3 py-1 rounded-md text-sm font-medium"
                                [style.backgroundColor]="getAlternateBgColor(i)"
                                [style.color]="getAlternateTextColor(i)" [pTooltip]="altKey" tooltipPosition="top">
                                {{ altKey }}
                            </span>
                        </div>
                        }
                    </div>

                    <!-- DATA ROWS -->
                    @for (row of filteredLayerVisualizationData.rows; track row.layerName; let rowIndex = $index) {
                    <div class="flex items-stretch">
                        <!-- Sticky layer name cell -->
                        <div class="layer-visualization-width shrink-0 text-sm text-gray-700 pl-4 bg-white sticky left-0 z-50 flex items-center whitespace-nowrap"
                            style="z-index: 50;">
                            {{ row.layerName }}
                        </div>

                        @for (cell of row.cells; track $index) {
                        <div class="w-[9rem] shrink-0 px-6 text-center flex flex-col items-center justify-end relative"
                            [style.zIndex]="(filteredLayerVisualizationData.rows.length - rowIndex) * 2">
                            @if (hasPositiveDepth(cell.depth)) {
                            <div class="inline-flex flex-col items-center justify-end cursor-pointer"
                                (mouseenter)="onLayerCellEnter($event, cell, row.layerName)"
                                (mouseleave)="onLayerCellLeave()">
                                <div class="relative">
                                    <div style="position:relative; z-index:2;">
                                        <!-- Same pre-sanitized cache used by the main visualisation -->
                                        <span [innerHTML]="layerSvgCache.get(rowIndex + '-' + cell.depth)"
                                            class="block"></span>
                                        <span
                                            class="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-none">

                                            <span
                                                class="bg-gray-900/80 text-white text-[10px] font-semibold leading-none px-2 py-1 rounded-full whitespace-nowrap">
                                                {{ formatDepthDisplay(cell.depth) }} in.
                                            </span>
                                            @if (getMethodChipName(cell.stabilizationMethods); as chip) {
                                            <span class="inline-flex items-center gap-1">
                                                <span
                                                    class="text-[10px] font-semibold leading-none px-2 py-1 whitespace-nowrap {{ chip.classes }}">
                                                    {{ chip.label }}
                                                </span>
                                                @if (chip.extraCount > 0) {
                                                <span
                                                    class="text-[10px] font-semibold leading-none px-2 py-1 whitespace-nowrap rounded-xl text-white bg-[#475569] cursor-pointer pointer-events-auto"
                                                    [pTooltip]="chip.extraTooltip" tooltipPosition="top">
                                                    +{{ chip.extraCount }}
                                                </span>
                                                }
                                            </span>
                                            }
                                        </span>


                                    </div>
                                    <!-- position:absolute keeps the method image out of flow. -->
                                    @if (getMethodSvgSrc(cell.stabilizationMethods); as methodSrc) {
                                    <img [src]="methodSrc"
                                        style="position:absolute; z-index:1; bottom:-16px; left:50%; transform:translateX(-50%); width:136px; display:block;"
                                        alt="stabilization method" />
                                    }
                                </div>
                            </div>
                            } @else {
                            <!-- Depth is 0 or missing -->
                            <span class="text-gray-400 text-sm select-none cursor-default"
                                (mouseenter)="onLayerCellEnter($event, cell, row.layerName)"
                                (mouseleave)="onLayerCellLeave()">—</span>
                            }
                        </div>
                        }
                    </div>
                    }

                    <!-- Dummy row to cover the last row's overhanging method SVG -->
                    <div class="flex items-stretch">
                        <div class="layer-visualization-width shrink-0 bg-white sticky left-0 z-50 h-6"
                            style="z-index: 50;"></div>
                        <div class="flex-1 h-6"></div>
                    </div>
                </div>
            </div>
            }
        </div>


        <!-- Alternate Leader Board -->
        <div class="flex flex-col gap-2 bg-[#E9EDFB]/60 p-3 rounded-2xl">
            <div class="flex items-center justify-between gap-3 mb-1">
                <div class="flex items-center gap-2">
                    <h3 class="text-md font-semibold">Alternate leaderboard</h3>
                    <app-icon type="info" size="sm" class="text-gray-500"
                        pTooltip=" Visually review the selected alternate through a concise summary of cost distribution, schedule, and unit cost"
                        tooltipPosition="top"></app-icon>
                </div>

                <div class="flex gap-2">
                    <!-- filterRankBy -->
                    <div class="flex items-center gap-2">
                        <div class="flex items-center gap-2">
                            <div class="text-xs font-semibold text-gray-500 italic">Ranked by {{ filterRankBy == 'cost'
                                ? 'cost' :
                                'schedule' }}</div>
                            <app-menu #alternateMenu [items]="alternateOptions"
                                (itemSelected)="onAlternateSelect($event)">
                                <app-button [label]="selectedAlternateLabel" icon="chevron-down" iconPosition="right"
                                    iconSource="lucide" variant="outlined" severity="contrast"
                                    (clicked)="alternateMenu.toggle($event)">
                                </app-button>
                            </app-menu>
                        </div>
                        <app-button icon="sliders-horizontal" iconSource="lucide" variant="outlined" severity="contrast"
                            (clicked)="openNewAlternateDialog()">
                        </app-button>
                    </div>


                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                <!-- Total cost -->
                <div class="p-3 rounded-2xl border bg-white flex flex-col items-start justify-between shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-arena-orange-200 rounded-md p-1">
                            <app-icon type="circle-dollar-sign" size="sm" class="text-orange-500" />
                        </span>
                        <span class="text-sm text-gray-500">Total cost</span>
                    </div>
                    @if (leaderboardLoading) {
                    <p-skeleton width="70%" height="1.75rem" class="rounded-md"></p-skeleton>
                    } @else {
                    <p class="text-lg font-bold text-gray-900 leading-tight m-0">{{
                        selectedLeaderboardData.totalCost.value | currency:'USD':'symbol':'1.2-2' }}</p>
                    }
                </div>

                <!-- Cost mix -->
                <div class="p-3 rounded-2xl border bg-white flex flex-col shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-indigo-100 rounded-md p-1">
                            <app-icon type="chart-pie" size="sm" class="text-indigo-500" />
                        </span>
                        <span class="text-sm text-gray-500">Cost mix</span>
                    </div>
                    <div class="grid grid-cols-3 gap-1 mt-1">
                        <div class="flex items-center gap-1 min-w-0 overflow-hidden" pTooltip="Material">
                            @if (leaderboardLoading) {
                            <p-skeleton width="100%" height="1.5rem" class="rounded-md"></p-skeleton>
                            } @else {
                            <app-icon type="brick-wall" size="sm" class="text-indigo-500 shrink-0" />
                            <p class="text-base font-bold text-gray-900 leading-tight m-0 truncate">{{
                                selectedLeaderboardData.costMix.material }}%</p>
                            }
                        </div>
                        <div class="flex items-center gap-1 min-w-0 overflow-hidden justify-center"
                            pTooltip="Equipment">
                            @if (leaderboardLoading) {
                            <p-skeleton width="100%" height="1.5rem" class="rounded-md"></p-skeleton>
                            } @else {
                            <app-icon type="wrench" size="sm" class="text-indigo-500 shrink-0" />
                            <p class="text-base font-bold text-gray-900 leading-tight m-0 truncate">{{
                                selectedLeaderboardData.costMix.equipment }}%</p>
                            }
                        </div>
                        <div class="flex items-center gap-1 min-w-0 overflow-hidden justify-end" pTooltip="Labor">
                            @if (leaderboardLoading) {
                            <p-skeleton width="100%" height="1.5rem" class="rounded-md"></p-skeleton>
                            } @else {
                            <app-icon type="user-round" size="sm" class="text-indigo-500 shrink-0" />
                            <p class="text-base font-bold text-gray-900 leading-tight m-0 truncate">{{
                                selectedLeaderboardData.costMix.labor }}%</p>
                            }
                        </div>
                    </div>
                </div>

                <!-- Schedule impact -->
                <div class="p-3 rounded-2xl border bg-white flex flex-col items-start justify-between shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-arena-blue-200 rounded-md p-1">
                            <app-icon type="calendar" size="sm" class="text-arena-blue-700" />
                        </span>
                        <span class="text-sm text-gray-500">Estimated schedule</span>
                    </div>
                    @if (leaderboardLoading) {
                    <p-skeleton width="60%" height="1.75rem" class="rounded-md"></p-skeleton>
                    } @else {
                    <p class="text-lg font-bold text-gray-900 leading-tight m-0">{{
                        selectedLeaderboardData.scheduleImpact }} Days</p>
                    }
                </div>

                <!-- General works cost -->
                <div class="p-3 rounded-2xl border bg-white flex flex-col items-start justify-between shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-indigo-200 rounded-md p-1">
                            <app-icon type="building-2" size="sm" class="text-indigo-500" />
                        </span>
                        <span class="text-sm text-gray-500">General works cost</span>
                    </div>
                    @if (leaderboardLoading) {
                    <p-skeleton width="70%" height="1.75rem" class="rounded-md"></p-skeleton>
                    } @else {
                    <p class="text-lg font-bold text-gray-900 leading-tight m-0">{{
                        selectedLeaderboardData.generalWorksCost | currency:'USD':'symbol':'1.2-2' }}</p>
                    }
                </div>

                <!-- Stabilization works cost -->
                <div class="p-3 rounded-2xl border bg-white flex flex-col items-start justify-between shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-pink-200 rounded-md p-1">
                            <app-icon type="layers" size="sm" class="text-pink-500" />
                        </span>
                        <span class="text-sm text-gray-500">Stabilization works cost</span>
                    </div>
                    @if (leaderboardLoading) {
                    <p-skeleton width="70%" height="1.75rem" class="rounded-md"></p-skeleton>
                    } @else {
                    <p class="text-lg font-bold text-gray-900 leading-tight m-0">{{
                        selectedLeaderboardData.stabilizationWorksCost | currency:'USD':'symbol':'1.2-2' }}</p>
                    }
                </div>

                <!-- Total unit cost -->
                <div class="p-3 rounded-2xl bg-[#7789C7] flex flex-col items-start justify-between shadow-sm">
                    <div class="flex items-center gap-2 mb-2">
                        <span class="bg-[#E9EDFB] rounded-md p-1">
                            <app-icon type="package" size="sm" class="text-[#7789C7]" />
                        </span>
                        <span class="text-sm text-white font-medium">Total unit cost</span>
                    </div>
                    @if (leaderboardLoading) {
                    <p-skeleton width="10rem" height="1.5rem" class="rounded-md"></p-skeleton>
                    } @else {
                    <p class="text-lg font-bold text-white m-0">{{ selectedLeaderboardData.totalUnitCost |
                        currency:'USD':'symbol':'1.2-2' }} / Sq.Yard</p>
                    }
                </div>

            </div>
        </div>

        <!-- Resource cost breakdown -->
        <div class="bg-white  p-3 rounded-2xl border border-gray-200/80">
            <div class="flex items-center justify-between mb-4">
                <div class="flex gap-2 items-center">
                    <h3 class=" text-lg font-semibold">Resource cost breakdown</h3>
                    <app-icon type="info" size="sm" class="text-gray-500"
                        pTooltip=" View how the selected work item is applied across different alternates and compare the associated cost distribution across material, labor, and equipment"
                        tooltipPosition="top"></app-icon>
                </div>
                <div class="flex items-center gap-2">
                    @if (resourceCostView === 'table') {
                    <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                        severity="contrast" pTooltip="Expand" tooltipPosition="top"
                        (clicked)="expandedDashboardTable = 'resourceCost'">
                    </app-button>
                    }
                    <div class="flex items-center border rounded-lg overflow-hidden">
                        <button class="p-2 transition-colors border-r" [class.bg-gray-100]="resourceCostView === 'card'"
                            [class.text-gray-800]="resourceCostView === 'card'"
                            [class.text-gray-400]="resourceCostView !== 'card'" (click)="resourceCostView = 'card'">
                            <app-icon type="layout-grid" source="lucide" size="sm"></app-icon>
                        </button>
                        <button class="p-2 transition-colors" [class.bg-gray-100]="resourceCostView === 'table'"
                            [class.text-gray-800]="resourceCostView === 'table'"
                            [class.text-gray-400]="resourceCostView !== 'table'" (click)="resourceCostView = 'table'">
                            <app-icon [type]="'pi pi-bars'" [source]="'primeng'" size="sm"></app-icon>
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-2 mb-5">
                <div class="flex items-center gap-6">
                    <span class="text-sm text-gray-500 w-36 shrink-0">General works</span>
                    <div class="flex gap-2">
                        @if (leaderboardLoading) {
                        @for (s of [1,2,3]; track s) {
                        <p-skeleton width="5rem" height="2rem" class="rounded-base"></p-skeleton>
                        }
                        } @else {
                        @for (tab of generalWorksTabs; track tab) {
                        <div class="px-3 py-1.5 text-sm rounded-base border transition-colors cursor-pointer"
                            [class.border-blue-300]="selectedGeneralWorksTab === tab"
                            [class.bg-blue-50]="selectedGeneralWorksTab === tab"
                            [class.font-medium]="selectedGeneralWorksTab === tab"
                            [class.border-gray-200]="selectedGeneralWorksTab !== tab"
                            [class.text-gray-500]="selectedGeneralWorksTab !== tab"
                            (click)="onSelectResourceTab(tab, 'general')">
                            {{ tab }}
                        </div>
                        }
                        }
                    </div>
                </div>
                <div class="flex items-center gap-6">
                    <span class="text-sm text-gray-500 w-36 shrink-0">Stabilization works</span>
                    <div class="flex gap-2">
                        @if (leaderboardLoading) {
                        @for (s of [1,2,3]; track s) {
                        <p-skeleton width="5rem" height="2rem" class="rounded-base"></p-skeleton>
                        }
                        }@else {
                        @for (tab of stabilizationTabs; track tab) {
                        <div class="px-3 py-1.5 text-sm rounded-base border transition-colors cursor-pointer"
                            [class.border-blue-300]="selectedStabilizationTab === tab"
                            [class.bg-blue-50]="selectedStabilizationTab === tab"
                            [class.font-medium]="selectedStabilizationTab === tab"
                            [class.border-gray-200]="selectedStabilizationTab !== tab"
                            [class.text-gray-500]="selectedStabilizationTab !== tab"
                            (click)="onSelectResourceTab(tab, 'stabilization')">
                            {{ tab }}
                        </div>
                        }
                        }
                    </div>
                </div>
            </div>

            @if (resourceCostView === 'card') {
            <div class="grid grid-cols-2 gap-4">
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Cost breakdown across alternates</p>
                    <div echarts [options]="costBreakdownChartOption" class="h-64 z-10000" appendTo="body"></div>
                    <p class="text-xs text-gray-400 mt-2 text-center">Total cost of selected work</p>
                </div>
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Unit cost by alternates</p>
                    <div echarts [options]="unitCostByAlternatesChartOption" class="h-64 z-10000" appendTo="body"></div>
                    <p class="text-xs text-gray-400 mt-2 text-center">Unit cost of selected work</p>
                </div>
            </div>
            }

            @if (resourceCostView === 'table') {
            <div class="h-80 overflow-y-auto">
                @if (leaderboardLoading) {
                <div class="flex flex-col gap-2 p-2">
                    <p-skeleton width="100%" height="2rem" class="rounded-md"></p-skeleton>
                    @for (r of [1,2,3,4,5]; track r) {
                    <p-skeleton width="100%" height="1.75rem" class="rounded-md"></p-skeleton>
                    }
                </div>
                } @else {
                <app-data-table [data]="resourceCostTableData" [columns]="resourceCostTableColumns"
                    [config]="resourceCostTableConfig">
                </app-data-table>
                }
            </div>
            }
        </div>

        <!-- Work cost breakdown -->
        <div class="bg-white  p-3 rounded-2xl border border-gray-200/80">
            <div class="flex items-center justify-between mb-5">
                <div class="flex items-center gap-2">
                    <h3 class="text-lg font-semibold">Work cost breakdown</h3>
                    <app-icon type="info" size="sm" class="text-gray-500"
                        pTooltip=" Compare total and unit costs across alternates based on the applied works Consists of 2 Graphs"
                        tooltipPosition="top"></app-icon>
                </div>
                <div class="flex items-center gap-2">
                    @if (workCostView === 'table') {
                    <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                        severity="contrast" pTooltip="Expand" tooltipPosition="top"
                        (clicked)="expandedDashboardTable = 'workCost'">
                    </app-button>
                    }
                    <div class="flex items-center border rounded-lg overflow-hidden">
                        <button class="p-2 transition-colors border-l" [class.bg-gray-100]="workCostView === 'card'"
                            [class.text-gray-800]="workCostView === 'card'"
                            [class.text-gray-400]="workCostView !== 'card'" (click)="workCostView = 'card'">
                            <app-icon type="layout-grid" source="lucide" size="sm"></app-icon>
                        </button>
                        <button class="p-2 transition-colors" [class.bg-gray-100]="workCostView === 'table'"
                            [class.text-gray-800]="workCostView === 'table'"
                            [class.text-gray-400]="workCostView !== 'table'" (click)="workCostView = 'table'">
                            <app-icon [type]="'pi pi-bars'" [source]="'primeng'" size="sm"></app-icon>
                        </button>
                    </div>
                </div>
            </div>

            @if (workCostView === 'card') {
            <div class="grid grid-cols-2 gap-4">
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Total cost across alternates</p>
                    <div echarts [options]="totalCostAcrossAlternatesChartOption" class="h-64"></div>
                    <p class="text-xs text-gray-400 mt-2 text-center">Alternate total cost by Work</p>
                </div>
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Unit cost across alternates</p>
                    <div echarts [options]="unitCostAcrossAlternatesChartOption" class="h-64"></div>
                    <p class="text-xs text-gray-400 mt-2 text-center">Alternate unit cost</p>
                </div>
            </div>
            }

            @if (workCostView === 'table') {
            <div class="h-80 overflow-y-auto">
                @if (leaderboardLoading) {
                <div class="flex flex-col gap-2 p-2">
                    <p-skeleton width="100%" height="2rem" class="rounded-md"></p-skeleton>
                    @for (r of [1,2,3,4,5]; track r) {
                    <p-skeleton width="100%" height="1.75rem" class="rounded-md"></p-skeleton>
                    }
                </div>
                } @else {
                <app-data-table [data]="workCostTableData" [columns]="workCostTableColumns"
                    [config]="workCostTableConfig">
                </app-data-table>
                }
            </div>
            }
        </div>


        <!-- Impact vs Alternate -->
        <div class="bg-white  p-3 rounded-2xl border border-gray-200/80 ">
            <div class="flex items-center justify-between mb-5">
                <div class="flex items-center gap-2">
                    <h3 class="text-lg font-semibold">Impact vs Alternate</h3>
                    <app-icon type="info" size="sm" class="text-gray-500"
                        pTooltip=" Compare the percentage change in schedule and cost for each alternate compared to the baseline configuration Consists of 2 Graphs"
                        tooltipPosition="top"></app-icon>
                </div>
                <div class="flex items-center gap-2">
                    @if (impactView === 'table') {
                    <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                        severity="contrast" pTooltip="Expand" tooltipPosition="top"
                        (clicked)="expandedDashboardTable = 'impact'">
                    </app-button>
                    }
                    <div class="flex items-center border rounded-lg overflow-hidden">
                        <button class="p-2 transition-colors border-r" [class.bg-gray-100]="impactView === 'card'"
                            [class.text-gray-800]="impactView === 'card'" [class.text-gray-400]="impactView !== 'card'"
                            (click)="impactView = 'card'">
                            <app-icon type="layout-grid" source="lucide" size="sm"></app-icon>
                        </button>
                        <button class="p-2 transition-colors" [class.bg-gray-100]="impactView === 'table'"
                            [class.text-gray-800]="impactView === 'table'"
                            [class.text-gray-400]="impactView !== 'table'" (click)="impactView = 'table'">
                            <app-icon [type]="'pi pi-bars'" [source]="'primeng'" size="sm"></app-icon>
                        </button>
                    </div>
                </div>
            </div>

            @if (impactView === 'card') {
            <div class="grid grid-cols-2 gap-4">
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Cost Impact (% Change)</p>
                    <div echarts [options]="costImpactChartOption" class="h-64"></div>
                </div>
                <div class="border rounded-xl p-4">
                    <p class="text-sm font-semibold text-gray-800 mb-3">Schedule Impact (% Change)</p>
                    <div echarts [options]="scheduleImpactChartOption" class="h-64"></div>
                </div>
            </div>
            }

            @if (impactView === 'table') {
            <div class="h-80 overflow-y-auto">
                @if (leaderboardLoading) {
                <div class="flex flex-col gap-2 p-2">
                    <p-skeleton width="100%" height="2rem" class="rounded-md"></p-skeleton>
                    @for (r of [1,2,3,4,5]; track r) {
                    <p-skeleton width="100%" height="1.75rem" class="rounded-md"></p-skeleton>
                    }
                </div>
                } @else {
                <app-data-table [data]="impactTableData" [columns]="impactTableColumns" [config]="impactTableConfig">
                </app-data-table>
                }
            </div>
            }
        </div>
    </div>
    }

    <!-- Analysis Summary view has been moved to its own dedicated tab (GeoTechnicalAnalysisSummaryTabComponent) -->
</div>


@if (layerTooltip) {
<div class="pointer-events-none fixed z-[9999] -translate-x-1/2" [style.left.px]="layerTooltip.x"
    [style.top.px]="layerTooltip.y">
    <div class="w-3 h-3 bg-white border-t border-l border-gray-100 rotate-45 mx-auto -mb-1.5 shadow-sm"></div>
    <div class="bg-white rounded-2xl px-5 py-4 shadow-xl border border-gray-100 min-w-44 text-left">
        <!-- Layer name header -->
        @if (layerTooltip.layerName) {
        <p class="text-sm font-bold text-gray-900 mb-3 leading-tight border-b border-gray-100 pb-2">
            {{ layerTooltip.layerName }}
        </p>
        }
        <p class="text-xs text-gray-400 mb-0.5 leading-none">Depth</p>
        @if (layerVisualizationLoading) {
        <p-skeleton width="6rem" height="1.25rem" class="rounded-md mb-3"></p-skeleton>
        } @else {
        <p class="text-base font-semibold text-gray-900 mb-3 leading-tight">
            {{ formatDepthDisplay(layerTooltip.depth) }} {{ +formatDepthDisplay(layerTooltip.depth) === 1 ? 'inch' : 'inches' }}
        </p> }
        <p class="text-xs text-gray-400 mb-0.5 leading-none">Stabilization Method</p>
        @if (layerVisualizationLoading) {
        <p-skeleton width="8rem" height="1.25rem" class="rounded-md mb-1"></p-skeleton>
        <p-skeleton width="6rem" height="1.25rem" class="rounded-md"></p-skeleton>
        } @else {
        @for (method of layerTooltip.stabilizationMethods; track method.label) {
        <p class="text-base font-semibold text-gray-900 leading-tight">{{ method.value || '-' }}</p>
        }
        }
    </div>
</div>
}

<!-- Layer Visualization Expand Dialog -->
<app-dialog [visible]="expandedDashboardTable === 'layerVisualization'" (closed)="expandedDashboardTable = null"
    title="Layer Visualization" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4">
            @if (filteredLayerVisualizationData) {
            <div class="overflow-x-auto scrollbar-thin">
                <div class="flex flex-col min-w-max">

                    <!-- HEADER ROW -->
                    <div class="flex items-stretch">
                        <!-- Sticky "Layers" header -->
                        <div class="layer-visualization-width shrink-0 text-left text-sm font-semibold text-gray-700 pb-4 pr-4 pl-4 bg-white sticky left-0 z-50 flex items-end"
                            style="z-index: 50;">
                            Layers
                        </div>
                        @for (altKey of filteredLayerVisualizationData.alternateKeys; track altKey; let i = $index) {
                        <div
                            class="w-[9rem] shrink-0 text-center pb-4 px-6 flex flex-col items-center justify-end gap-1">
                            @if (filteredLayerVisualizationData.suitableAlternateKey === altKey) {
                            <div class="relative inline-flex flex-col items-center mb-1">
                                <span
                                    class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white theme-bg">
                                    <i class="pi pi-check icon-sm mr-1"></i> Most suitable
                                </span>
                                <span class="block w-0 h-0"
                                    style="border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 6px solid #6b21a8;"></span>
                            </div>
                            }
                            <span class="inline-block px-2 py-1 rounded-md text-sm font-medium"
                                [style.backgroundColor]="getAlternateBgColor(i)"
                                [style.color]="getAlternateTextColor(i)">
                                {{ altKey }}
                            </span>
                        </div>
                        }
                    </div>

                    <!-- DATA ROWS -->
                    @for (row of filteredLayerVisualizationData.rows; track row.layerName; let rowIndex = $index) {
                    <div class="flex items-stretch">
                        <!-- Sticky layer name cell -->
                        <div class="layer-visualization-width shrink-0 text-sm text-gray-700 pr-4 pl-4 bg-white sticky left-0 z-50 flex items-center whitespace-nowrap"
                            style="z-index: 50;">
                            {{ row.layerName }}
                        </div>

                        @for (cell of row.cells; track $index) {
                        <div class="w-[9rem] shrink-0 px-6 text-center flex flex-col items-center justify-end relative"
                            [style.zIndex]="(filteredLayerVisualizationData.rows.length - rowIndex) * 2">
                            @if (hasPositiveDepth(cell.depth)) {
                            <div class="inline-flex flex-col items-center justify-end cursor-pointer"
                                (mouseenter)="onLayerCellEnter($event, cell, row.layerName)"
                                (mouseleave)="onLayerCellLeave()">
                                <div class="relative">
                                    <!-- z-index:2: cylinder paints above this row's own method image -->
                                    <div style="position:relative; z-index:2;">
                                        <!-- Same pre-sanitized cache used by the main visualisation -->
                                        <span [innerHTML]="layerSvgCache.get(rowIndex + '-' + cell.depth)"
                                            class="block"></span>
                                        <span
                                            class="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-none">
                                            @if (getMethodChipName(cell.stabilizationMethods); as chip) {
                                            <span class="inline-flex items-center gap-1">
                                                <span
                                                    class="text-[10px] font-semibold leading-none px-2 py-1 whitespace-nowrap {{ chip.classes }}">
                                                    {{ chip.label }}
                                                </span>
                                                @if (chip.extraCount > 0) {
                                                <span
                                                    class="text-[10px] font-semibold leading-none px-2 py-1 whitespace-nowrap rounded-xl text-white bg-[#475569] cursor-pointer pointer-events-auto"
                                                    [pTooltip]="chip.extraTooltip" tooltipPosition="top">
                                                    +{{ chip.extraCount }}
                                                </span>
                                                }
                                            </span>
                                            }
                                            <span
                                                class="bg-gray-900/80 text-white text-[10px] font-semibold leading-none px-2 py-1 rounded-full whitespace-nowrap">
                                                {{ formatDepthDisplay(cell.depth) }} in.
                                            </span>
                                        </span>
                                    </div>
                                    <!-- position:absolute keeps the method image out of flow. -->
                                    @if (getMethodSvgSrc(cell.stabilizationMethods); as methodSrc) {
                                    <img [src]="methodSrc"
                                        style="position:absolute; z-index:1; bottom:-18px; left:50%; transform:translateX(-50%); width:136px; display:block;"
                                        alt="stabilization method" />
                                    }
                                </div>
                            </div>
                            } @else {
                            <!-- Depth is 0 or missing -->
                            <span class="text-gray-400 text-sm select-none cursor-default"
                                (mouseenter)="onLayerCellEnter($event, cell, row.layerName)"
                                (mouseleave)="onLayerCellLeave()">—</span>
                            }
                        </div>
                        }
                    </div>
                    }

                    <!-- Dummy row to cover the last row's overhanging method SVG -->
                    <div class="flex items-stretch">
                        <div class="layer-visualization-width shrink-0 bg-white sticky left-0 z-50 h-6"
                            style="z-index: 50;"></div>
                        <div class="flex-1 h-6"></div>
                    </div>
                </div>
            </div>
            }
        </div>
    </ng-template>
</app-dialog>

<!-- Resource Cost Breakdown Expand Dialog -->
<app-dialog [visible]="expandedDashboardTable === 'resourceCost'" (closed)="expandedDashboardTable = null"
    title="Resource Cost Breakdown" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
            <app-data-table [data]="resourceCostTableData" [columns]="resourceCostTableColumns"
                [config]="resourceCostTableConfig" [showScrollBar]="true" />
        </div>
    </ng-template>
</app-dialog>

<!-- Work Cost Breakdown Expand Dialog -->
<app-dialog [visible]="expandedDashboardTable === 'workCost'" (closed)="expandedDashboardTable = null"
    title="Work Cost Breakdown" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
            <app-data-table [data]="workCostTableData" [columns]="workCostTableColumns" [config]="workCostTableConfig"
                [showScrollBar]="true" />
        </div>
    </ng-template>
</app-dialog>

<!-- Impact vs Alternate Expand Dialog -->
<app-dialog [visible]="expandedDashboardTable === 'impact'" (closed)="expandedDashboardTable = null"
    title="Impact vs Alternate" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
            <app-data-table [data]="impactTableData" [columns]="impactTableColumns" [config]="impactTableConfig"
                [showScrollBar]="true" />
        </div>
    </ng-template>
</app-dialog>

<!-- Analysis summary re-generate dialog has been moved to analysis-summary-tab.component.html -->


<app-dialog [(visible)]="showNewAlternateDialog" title="Add New Method" class=" max-w-xl">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-3 p-4">

            @if (newAlternateDialogLayers.length === 0) {
            <div class="text-sm text-gray-400 text-center py-6">
                No unapplied stabilization works available for this alternate.
            </div>
            }

            @for (layer of newAlternateDialogLayers; track layer.layerName) {
            <p-accordion [multiple]="true" class="add-new-method-accordion">
                <p-accordion-panel [value]="layer.layerName">
                    <p-accordion-header>
                        <span class="font-semibold text-sm text-gray-800">{{ layer.layerName }}</span>
                    </p-accordion-header>
                    <p-accordion-content>
                        @if (layer.works.length === 0) {
                        <p class="text-xs text-gray-400 px-1 py-2">
                            All stabilization works are already applied in this layer.
                        </p>
                        }
                        <p-accordion [multiple]="true" class="add-new-method-accordion">
                            @for (work of layer.works; track work.workName) {
                            <p-accordion-panel [value]="work.workName">
                                <p-accordion-header>
                                    <span class="text-sm font-medium text-gray-700">{{ work.workName }}</span>
                                </p-accordion-header>
                                <p-accordion-content>
                                    @if (work.methods.length === 0) {
                                    <p class="text-xs text-gray-400 px-1">No methods available.</p>
                                    }
                                    <div class="flex flex-col gap-2 px-1">
                                        @for (method of work.methods; track method.value) {
                                        <label class="flex items-center gap-2 cursor-pointer select-none">
                                            <p-radioButton [name]="work.workName" [value]="method.value"
                                                [(ngModel)]="work.selectedMethod" />
                                            <span class="text-sm text-gray-700">{{ method.label }}</span>
                                        </label>
                                        }
                                    </div>
                                </p-accordion-content>
                            </p-accordion-panel>
                            }
                        </p-accordion>
                    </p-accordion-content>
                </p-accordion-panel>
            </p-accordion>
            }
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button label="Cancel" variant="outlined" severity="contrast" (clicked)="showNewAlternateDialog = false"
                [disabled]="calculatingNewAlternate()" />
            <app-button label="Calculate Costs" variant="filled" severity="primary"
                [loading]="calculatingNewAlternate()" (clicked)="onSubmitNewAlternate()" />
        </div>
    </ng-template>
</app-dialog>